import { Injectable } from '@angular/core';
import { BaseService } from './base.service';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import moment from 'moment';
import { UserService } from './user.service';

export interface VideoHlsStartResponse {
  sessionId: string;
  playlistUrl: string;
}

export type VideoDownloadJobState =
  | 'PENDING'
  | 'DOWNLOADING_FROM_NVR'
  | 'TRANSCODING'
  | 'READY'
  | 'CANCELED'
  | 'FAILED';

export interface VideoDownloadStatus {
  jobId: string;
  state: VideoDownloadJobState;
  bytesDownloadedFromNvr: number;
  totalBytesExpected: number | null;
  percentComplete: number | null;
  errorMessage: string | null;
}

export interface EquipmentVideoRequestFilters {
  deviceId:  string;
  laneId:    string;
  startDate: Date;
  endDate?:   Date;
  duration?: number;
}

export interface ReportRecord {
  id: number;
  idEquipamento: string;
  laneLabel: string;
  nomeOperador: string;
  dataHora: string;
  dataHoraInicioVideo: string;
  dataHoraFimVideo: string;
  contagemTotal: number;
  contagemEsperada: number;
}

export interface SavePassageCountPayload {
  idEquipamento: string;
  laneLabel: string;
  idOperador: string;
  dataHora: string;
  dataHoraInicioVideo: string;
  dataHoraFimVideo: string;
  contagemTotal: number;
}

export interface ReportSearchFilters {
  idEquipamento?: string;
  laneLabel?: string;
  dataHoraInicio?: string;
  dataHoraFim?: string;
}

export interface ReportPageResponse {
  content?: ReportRecord[];
  totalElements?: number;
  totalRecords?: number;
  totalItems?: number;
  totalContent?: number;
}

@Injectable({ providedIn: 'root' })
export class EquipmentVideoService extends BaseService {
  constructor(
    public override httpClient: HttpClient,
    private userService: UserService
  ) {
    super(httpClient);
  }

  startEquipmentVideoHls(filters: EquipmentVideoRequestFilters): Observable<VideoHlsStartResponse> {
    const baseUrl = this.getEndpoint('EQUIPMENT_VIDEO_STREAM');
    const { startDateStr, endDateStr, laneQuery } = this.buildVideoQueryParams(filters);
    const url = `${baseUrl}${encodeURIComponent(filters.deviceId)}/video-hls`;
    const query = `?startDate=${encodeURIComponent(startDateStr)}&endDate=${encodeURIComponent(endDateStr)}${laneQuery}`;

    return this.httpClient.post<{ sessionId: string; playlistUrl: string }>(
      `${url}${query}`,
      {},
      { headers: this.getVideoDownloadHeaders() }
    ).pipe(
      map(response => ({
        sessionId: response.sessionId,
        playlistUrl: this.resolveHlsUrl(response.playlistUrl, baseUrl)
      }))
    );
  }

  cancelEquipmentVideoHls(equipmentId: string, sessionId: string): Observable<void> {
    const url = `${this.getEndpoint('EQUIPMENT_VIDEO_STREAM')}${encodeURIComponent(equipmentId)}/video-hls/${encodeURIComponent(sessionId)}`;
    return this.httpClient.delete<void>(url, { headers: this.getVideoDownloadHeaders() });
  }

  getEquipmentVideoDownloadUrl(filters: EquipmentVideoRequestFilters): string {
    const baseUrl = this.getEndpoint('EQUIPMENT_VIDEO_DOWNLOAD');
    const { startDateStr, endDateStr, laneQuery } = this.buildVideoQueryParams(filters, false);
    return `${baseUrl}${filters.deviceId}/video-download?startDate=${encodeURIComponent(startDateStr)}&endDate=${encodeURIComponent(endDateStr)}${laneQuery}`;
  }

  startVideoDownload(filters: EquipmentVideoRequestFilters): Observable<VideoDownloadStatus> {
    const baseUrl = this.getEndpoint('EQUIPMENT_VIDEO_DOWNLOAD');
    const { startDateStr, endDateStr, laneQuery } = this.buildVideoQueryParams(filters, false);
    const url = `${baseUrl}${filters.deviceId}/video-download/start?startDate=${encodeURIComponent(startDateStr)}&endDate=${encodeURIComponent(endDateStr)}${laneQuery}`;
    return this.httpClient.post<VideoDownloadStatus>(url, {}, { headers: this.getVideoDownloadHeaders() });
  }

  cancelVideoDownload(equipmentId: string, jobId: string): Observable<VideoDownloadStatus> {
    const url = `${this.getEndpoint('EQUIPMENT_VIDEO_DOWNLOAD')}${equipmentId}/video-download/cancel/${encodeURIComponent(jobId)}`;
    return this.httpClient.post<VideoDownloadStatus>(url, {}, { headers: this.getVideoDownloadHeaders() });
  }

  getVideoDownloadStatus(equipmentId: string, jobId: string): Observable<VideoDownloadStatus> {
    const url = `${this.getEndpoint('EQUIPMENT_VIDEO_DOWNLOAD')}${equipmentId}/video-download/status/${encodeURIComponent(jobId)}`;
    return this.httpClient.get<VideoDownloadStatus>(url, { headers: this.getVideoDownloadHeaders() });
  }

  getVideoDownloadResult(equipmentId: string, jobId: string): Observable<HttpResponse<Blob>> {
    const url = `${this.getEndpoint('EQUIPMENT_VIDEO_DOWNLOAD')}${equipmentId}/video-download/result/${encodeURIComponent(jobId)}`;
    return this.httpClient.get(url, {
      headers: this.getVideoDownloadHeaders(),
      responseType: 'blob',
      observe: 'response'
    });
  }

  private getVideoDownloadHeaders(): HttpHeaders {
    const token = this.userService.getToken();
    return token ? new HttpHeaders({ Authorization: token.trim() }) : new HttpHeaders();
  }

  private resolveHlsUrl(playlistUrl: string, endpointBaseUrl: string): string {
    const endpoint = new URL(endpointBaseUrl, window.location.origin);
    const origin = `${endpoint.protocol}//${endpoint.host}`;
    return new URL(playlistUrl, origin).toString();
  }

  private buildVideoQueryParams(
    filters: EquipmentVideoRequestFilters,
    includeMargin: boolean = true
  ): { startDateStr: string; endDateStr: string; laneQuery: string } {
    const margin = includeMargin ? 5 : 0;
  
    const originalStartDate = moment(filters.startDate);
    const startDate = originalStartDate.clone().subtract(margin, 'seconds');
    const endDate = filters.duration
      ? originalStartDate
          .clone()
          .add(filters.duration, 'minutes')
          .add(margin, 'seconds')
      : moment(filters.endDate).add(margin, 'seconds');
  
    const startDateStr = startDate.format('YYYY-MM-DD HH:mm:ss');
    const endDateStr   = endDate.format('YYYY-MM-DD HH:mm:ss');
    const laneQuery    = filters.laneId ? `&laneLabel=${encodeURIComponent(filters.laneId)}` : '';
  
    return { startDateStr, endDateStr, laneQuery };
  }

  savePassageCount(payload: SavePassageCountPayload): Observable<any> {
    const url = this.getEndpoint('EQUIPMENT_VIDEO_COUNT');
    return this.postData(url, payload);
  }
  
  deletePassageCount(ids: number[]): Observable<any> {
    const url = `${this.getEndpoint('EQUIPMENT_VIDEO_COUNT')}/delete`;
    return this.postData(url, ids);
  }
  
  getReports(
    page: number = 0,
    size: number = 20,
    filters: ReportSearchFilters = {}
  ): Observable<ReportRecord[] | ReportPageResponse> {
    let params = new HttpParams()
    .set('page', String(page))
    .set('size', String(size));
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params = params.set(key, String(value));
      }
    });
    
    const url = `${this.getEndpoint('EQUIPMENT_VIDEO_COUNT')}?${params.toString()}`;
    return this.getData(url);
  }

}
